import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  TextInput,
  ActivityIndicator,
} from 'react-native';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { reportStrings, commonStrings } from '@/constants/residents/marketplaceStrings';
import { INPUT_LIMITS } from '@/constants/residents/marketplaceConstants';
import { ReportReason, REPORT_REASONS } from '@/types/residents/marketplaceListing';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { UnifiedModal } from '@/components/common/UnifiedModal/UnifiedModal';
import { RadioButton } from './shared';

const CheckIcon = RESIDENTS_SVG_COMPONENTS.CHECK;

interface ReportModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (reason: ReportReason, description?: string) => void;
  hasAlreadyReported?: boolean;
  isSubmitting?: boolean;
}

export const ReportModal: React.FC<ReportModalProps> = ({
  visible,
  onClose,
  onSubmit,
  hasAlreadyReported = false,
  isSubmitting = false,
}) => {
  const MARKETPLACE_COLORS = useMarketplaceColors();
  const [selectedReason, setSelectedReason] = useState<ReportReason | null>(null);
  const [description, setDescription] = useState('');

  const handleSubmit = () => {
    if (selectedReason && !isSubmitting) {
      onSubmit(selectedReason, description.trim() || undefined);
      // Reset form
      setSelectedReason(null);
      setDescription('');
    }
  };

  const handleClose = () => {
    // Reset form on close
    setSelectedReason(null);
    setDescription('');
    onClose();
  };

  // Already reported state
  if (hasAlreadyReported) {
    const footer = (
      <TouchableOpacity
        style={[styles.submitButton, { backgroundColor: MARKETPLACE_COLORS.buttonPrimary }]}
        onPress={handleClose}
        activeOpacity={0.8}
      >
        <Text style={[styles.submitButtonText, { color: MARKETPLACE_COLORS.background }]}>{commonStrings.close}</Text>
      </TouchableOpacity>
    );

    return (
      <UnifiedModal
        visible={visible}
        onClose={handleClose}
        variant="dialog"
        showHeader={true}
        headerStyle={{ borderBottomWidth: 0 }}
        footer={footer}
      >
        <View style={styles.alreadyReportedContent}>
          {/* Success Icon */}
          <View style={styles.successIconContainer}>
            <View style={[styles.successIconOuter, { backgroundColor: MARKETPLACE_COLORS.accentBlueLight }]}>
              <View style={[styles.successIconInner, { backgroundColor: MARKETPLACE_COLORS.accentBlueSolid }]}>
                <CheckIcon width={32} height={32} color={MARKETPLACE_COLORS.textPrimary} />
              </View>
            </View>
          </View>

          <Text style={[styles.alreadyReportedTitle, { color: MARKETPLACE_COLORS.textPrimary }]}>{reportStrings.reportSubmitted}</Text>
          <Text style={[styles.alreadyReportedDescription, { color: MARKETPLACE_COLORS.textSecondary }]}>
            {reportStrings.alreadyReported}
          </Text>
        </View>
      </UnifiedModal>
    );
  }

  // Report form state
  const isDisabled = !selectedReason || isSubmitting;
  const footer = (
    <TouchableOpacity
      style={[
        styles.submitButton,
        { backgroundColor: MARKETPLACE_COLORS.buttonPrimary },
        isDisabled && styles.submitButtonDisabled,
        isDisabled && { backgroundColor: MARKETPLACE_COLORS.buttonDisabled },
      ]}
      onPress={handleSubmit}
      activeOpacity={0.8}
      disabled={isDisabled}
    >
      {isSubmitting ? (
        <ActivityIndicator size="small" color={MARKETPLACE_COLORS.background} />
      ) : (
        <Text style={[
          styles.submitButtonText,
          { color: MARKETPLACE_COLORS.background },
          isDisabled && styles.submitButtonTextDisabled,
          isDisabled && { color: MARKETPLACE_COLORS.buttonDisabledText },
        ]}>
          {commonStrings.submit}
        </Text>
      )}
    </TouchableOpacity>
  );

  return (
    <UnifiedModal
      visible={visible}
      onClose={handleClose}
      variant="dialog"
      showHeader={true}
      headerStyle={{ borderBottomWidth: 0 }}
      footer={footer}
      scrollable={true}
      avoidKeyboard={true}
    >
      {/* Title and Description */}
      <View style={styles.titleSection}>
        <Text style={[styles.title, { color: MARKETPLACE_COLORS.textPrimary }]}>{reportStrings.title}</Text>
        <Text style={[styles.subtitle, { color: MARKETPLACE_COLORS.textSecondary }]}>
          {reportStrings.subtitle}
        </Text>
      </View>

      {/* Report Options */}
      <View style={styles.optionsList}>
        {REPORT_REASONS.map((reason) => (
          <TouchableOpacity
            key={reason.value}
            style={[styles.optionItem, { backgroundColor: MARKETPLACE_COLORS.white5Percent }]}
            onPress={() => setSelectedReason(reason.value)}
            activeOpacity={0.8}
          >
            <Text style={[
              styles.optionLabel,
              { color: MARKETPLACE_COLORS.textPrimary },
              selectedReason === reason.value && styles.optionLabelSelected,
            ]}>
              {reason.label}
            </Text>
            <RadioButton isSelected={selectedReason === reason.value} />
          </TouchableOpacity>
        ))}

        {/* Description Input */}
        <View style={[styles.descriptionContainer, { backgroundColor: MARKETPLACE_COLORS.inputBackground, borderColor: MARKETPLACE_COLORS.inputBorder }]}>
          <TextInput
            style={[styles.descriptionInput, { color: MARKETPLACE_COLORS.textPrimary }]}
            placeholder={reportStrings.descriptionOptional}
            placeholderTextColor={MARKETPLACE_COLORS.textSecondary}
            value={description}
            onChangeText={setDescription}
            multiline
            textAlignVertical="top"
            maxLength={INPUT_LIMITS.reportDescription}
          />
        </View>
      </View>
    </UnifiedModal>
  );
};

const styles = StyleSheet.create({
  titleSection: {
    alignItems: 'center',
    gap: 6,
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: '600',
    lineHeight: 36,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 24,
    textAlign: 'center',
  },
  optionsList: {
    gap: 16,
  },
  optionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  optionLabel: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 21,
    flex: 1,
  },
  optionLabelSelected: {
    fontWeight: '500',
  },
  descriptionContainer: {
    borderRadius: 8,
    borderWidth: 1,
    height: 80,
  },
  descriptionInput: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 11,
    paddingBottom: 11,
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 17.5,
  },
  submitButton: {
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  submitButtonDisabled: {},
  submitButtonText: {
    fontSize: 12,
    fontWeight: '500',
    lineHeight: 18,
  },
  submitButtonTextDisabled: {},
  // Already Reported State
  alreadyReportedContent: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    paddingHorizontal: 24,
  },
  successIconContainer: {
    marginBottom: 10,
  },
  successIconOuter: {
    width: 100,
    height: 100,
    borderRadius: 50,
    alignItems: 'center',
    justifyContent: 'center',
  },
  successIconInner: {
    width: 70,
    height: 70,
    borderRadius: 35,
    alignItems: 'center',
    justifyContent: 'center',
  },
  alreadyReportedTitle: {
    fontSize: 24,
    fontWeight: '600',
    lineHeight: 36,
    textAlign: 'center',
  },
  alreadyReportedDescription: {
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 24,
    textAlign: 'center',
  },
});
